import { ErrorHandler, Injectable } from '@angular/core';

/**
 * Global Angular ErrorHandler.
 *
 * This is the last line of defense when something in the app throws
 * uncaught. It must keep working even if Angular's DI/rendering is
 * otherwise broken, so it:
 *  - never depends on Transloco/i18n (plain hardcoded English copy),
 *  - manipulates the DOM directly instead of rendering a component,
 *  - inlines all styling instead of relying on app stylesheets/Tailwind.
 *
 * It never swallows errors: `console.error` is always called first so
 * developers still see the original error in devtools/logs.
 */
const FALLBACK_OVERLAY_ID = 'app-crash-fallback-overlay';
let expectedAppTeardown = false;

/**
 * Authentication teardown can make active listeners reject while the browser
 * is leaving the app. Those errors are expected and must not replace the
 * logout redirect with the crash fallback.
 */
export function beginExpectedAppTeardown(): void {
  expectedAppTeardown = true;
}

export function endExpectedAppTeardown(): void {
  expectedAppTeardown = false;
}

@Injectable()
export class AppErrorHandler implements ErrorHandler {
  handleError(error: unknown): void {
    // Never swallow — surface to devtools/log collectors as before.
    console.error(error);

    if (expectedAppTeardown) {
      return;
    }

    this.showFallback();
  }

  private showFallback(): void {
    if (typeof document === 'undefined') {
      return;
    }

    // Guard against error storms: only ever inject one overlay.
    if (document.getElementById(FALLBACK_OVERLAY_ID)) {
      return;
    }

    const overlay = document.createElement('div');
    overlay.id = FALLBACK_OVERLAY_ID;
    overlay.setAttribute(
      'style',
      [
        'position:fixed',
        'inset:0',
        'z-index:2147483647',
        'display:flex',
        'align-items:center',
        'justify-content:center',
        'background:var(--background-color,#ffffff)',
        'font-family:Inter,system-ui,sans-serif',
      ].join(';'),
    );

    const card = document.createElement('div');
    card.setAttribute(
      'style',
      [
        'background:var(--background-color,#ffffff)',
        'color:var(--text-color,#18191a)',
        'border:1px solid var(--border-color,#e3e3e3)',
        'border-radius:2rem',
        'box-shadow:0 25px 50px -12px rgba(0,0,0,0.25)',
        'padding:1.5rem 1.75rem',
        'max-width:32rem',
        'width:calc(100% - 2rem)',
        'text-align:left',
      ].join(';'),
    );

    const message = document.createElement('p');
    message.textContent = 'Something went wrong.';
    message.setAttribute('style', 'margin:0 0 1.25rem;font-size:0.95rem;line-height:1.4;');

    const reloadButton = document.createElement('button');
    reloadButton.type = 'button';
    reloadButton.textContent = 'Reload';
    reloadButton.setAttribute(
      'style',
      [
        'background:var(--text-color,#18191a)',
        'color:var(--background-color,#ffffff)',
        'border:none',
        'border-radius:9999px',
        'padding:0.5rem 1.25rem',
        'width:100%',
        'font-size:0.9rem',
        'font-weight:600',
        'cursor:pointer',
      ].join(';'),
    );
    reloadButton.addEventListener('click', () => window.location.reload());

    card.appendChild(message);
    card.appendChild(reloadButton);
    overlay.appendChild(card);
    document.body.appendChild(overlay);
  }
}
